import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Note, CreateNoteData, UpdateNoteData } from '../../services/noteService';
import { useTranslation } from '../../i18n';

interface NoteEditorProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: CreateNoteData | UpdateNoteData) => Promise<void>;
    note?: Note | null;
    courseId?: number;
    lessonId?: number;
}

const NOTE_COLORS = [
    { value: null, label: 'None', color: '#e0e0e0' },
    { value: '#ffeb3b', label: 'Yellow', color: '#ffeb3b' },
    { value: '#4caf50', label: 'Green', color: '#4caf50' },
    { value: '#2196f3', label: 'Blue', color: '#2196f3' },
    { value: '#f44336', label: 'Red', color: '#f44336' },
    { value: '#9c27b0', label: 'Purple', color: '#9c27b0' },
    { value: '#ff9800', label: 'Orange', color: '#ff9800' },
];

const NoteEditor: React.FC<NoteEditorProps> = ({
    open,
    onClose,
    onSave,
    note,
    courseId,
    lessonId,
}) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [color, setColor] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const isEditing = !!note;

    useEffect(() => {
        if (note) {
            setTitle(note.title || '');
            setContent(note.content);
            setColor(note.color);
        } else {
            setTitle('');
            setContent('');
            setColor(null);
        }
    }, [note, open]);

    const handleSave = async () => {
        if (!content.trim()) return;

        setSaving(true);
        try {
            if (isEditing) {
                const updateData: UpdateNoteData = {
                    title: title || undefined,
                    content,
                    color: color || undefined,
                };
                await onSave(updateData);
            } else {
                if (!courseId) return;
                const createData: CreateNoteData = {
                    courseId,
                    lessonId,
                    title: title || undefined,
                    content,
                    color: color || undefined,
                };
                await onSave(createData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving note:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleColorChange = (
        _event: React.MouseEvent<HTMLElement>,
        newColor: string | null,
    ) => {
        setColor(newColor);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {isEditing ? t('notes.editNote') : t('notes.newNote')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('notes.title')}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder={t('notes.titlePlaceholder')}
                    />

                    <TextField
                        label={t('notes.content')}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        fullWidth
                        multiline
                        rows={6}
                        required
                        placeholder={t('notes.contentPlaceholder')}
                    />

                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('notes.color')}
                        </Typography>
                        <ToggleButtonGroup
                            value={color}
                            exclusive
                            onChange={handleColorChange}
                            size="small"
                        >
                            {NOTE_COLORS.map((c) => (
                                <ToggleButton
                                    key={c.value || 'none'}
                                    value={c.value || ''}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: c.color,
                                        '&.Mui-selected': {
                                            backgroundColor: c.color,
                                            border: '2px solid',
                                            borderColor: 'primary.main',
                                        },
                                        '&:hover': {
                                            backgroundColor: c.color,
                                            opacity: 0.8,
                                        },
                                    }}
                                >
                                    &nbsp;
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !content.trim()}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    {saving ? t('common.saving') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NoteEditor;
